// UI Chat: send + busy state + open/close panel + msg helpers + onboarding
// conv persistence (localStorage). PR-5 (ADR-0005). Estratto a parità
// funzionale da coach/index.html.
//
// Dipendenze ESM:
//   prompts.js   → getSys
//   builders.js  → saveProfile
//   generators.js → genAll
//
// Dipendenze runtime (window.* / global scope chain):
//   window.S, window.currentUser
//   updateBadge / updateDL → ui-home inline (PR-6 future extraction)
//   api → core (esposto via window.api dal main.js)

import { getSys } from './prompts.js';
import { saveProfile } from './builders.js';
import { genAll } from './generators.js';

// ─── ONBOARDING CONV PERSISTENCE ───────────────────────────────────────────
// Persiste la conversazione di onboarding pre-[PROFILO] su localStorage per
// sopravvivere a refresh / crash tab / chiusura accidentale. Stato volatile
// same-device: il save DB definitivo parte solo quando l'AI emette [PROFILO]
// con tutti i campi raccolti (saveProfile in send()). Cleanup automatico a
// quel punto e al logout. Namespace per user.id: evita leak multi-account.
export function _onboardingKey() {
  return window.currentUser ? `coachai_onboarding_v1_${window.currentUser.id}` : null;
}
export function persistOnboardingConv() {
  const k = _onboardingKey();
  if (!k) return;
  try { localStorage.setItem(k, JSON.stringify(window.S.conv)); } catch (_) {}
}
export function hydrateOnboardingConv() {
  const k = _onboardingKey();
  if (!k) return null;
  try {
    const raw = localStorage.getItem(k);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : null;
  } catch (_) {
    localStorage.removeItem(k);
    return null;
  }
}
export function clearOnboardingConv() {
  const k = _onboardingKey();
  if (k) localStorage.removeItem(k);
}

// ─── CHAT ──────────────────────────────────────────────────────────────────
let busy = false;
// Getter esposto per consumo cross-modulo (es. ui-home.js setTile usa busy
// per disabilitare i bottoni 'Riprova' durante una generazione attiva).
// Non esponiamo la variabile direttamente per preservare l'incapsulamento
// del modulo (setBusy resta unica via di mutazione).
export function getBusy() { return busy; }

export async function send() {
  if (busy) return;
  const inp = document.getElementById('inp');
  const txt = inp.value.trim(); if (!txt) return;
  inp.value = ''; autoResize(inp);
  setBusy(true);
  addMsg('user', txt);
  window.S.conv.push({ role: 'user', content: txt });
  if (window.S.conv.length > 30) window.S.conv = window.S.conv.slice(-30);
  if (!window.S.profile) persistOnboardingConv();
  try {
    // opts.cache: il system prompt della chat (~4.7k token con qualsiasi
    // profilo) supera la soglia di 4096 token di Haiku 4.5, quindi il
    // prompt caching morde davvero. Verificabile con getLastUsage().
    const reply = await window.api(window.S.conv, getSys(window.S.profile), 1000, window.HAIKU, { cache: true });
    const pm = reply.match(/\[PROFILO\]([\s\S]*?)\[\/PROFILO\]/);
    const um = reply.match(/\[UPDATE_PROFILO\]([\s\S]*?)\[\/UPDATE_PROFILO\]/);
    const rm = reply.match(/\[RIGENERA\]([\s\S]*?)\[\/RIGENERA\]/);
    const clean = reply
      .replace(/\[PROFILO\][\s\S]*?\[\/PROFILO\]/g, '')
      .replace(/\[UPDATE_PROFILO\][\s\S]*?\[\/UPDATE_PROFILO\]/g, '')
      .replace(/\[RIGENERA\][\s\S]*?\[\/RIGENERA\]/g, '').trim();
    if (clean) {
      addMsg('ai', clean);
      window.S.conv.push({ role: 'assistant', content: clean });
      if (!window.S.profile && !pm) persistOnboardingConv();
    }
    if (pm) {
      try {
        window.S.profile = JSON.parse(pm[1].trim());
        await saveProfile(window.S.profile);
        clearOnboardingConv();
        updateBadge();
        await genAll(window.S.profile, '');
      } catch(e) { addSys('❌ Errore profilo: ' + e.message, 'err'); }
    } else {
      if (um && window.S.profile) {
        try {
          const upd = JSON.parse(um[1].trim());
          if (upd && typeof upd === 'object' && !Array.isArray(upd)) {
            window.S.profile = { ...window.S.profile, ...upd };
            await saveProfile(window.S.profile);
            updateBadge();
            updateDL(); // refresh tile stretch se stretching_preference è cambiato
            addSys('✓ Profilo aggiornato', 'ok');
          }
        } catch(e) { addSys('❌ Update profilo: ' + e.message, 'err'); }
      }
      if (rm) {
        try {
          const rg = JSON.parse(rm[1].trim());
          if (window.S.profile) {
            // Non chiudiamo la chat qui: genAll gestisce chat/home a seconda
            // che il profilo sia completo o manchino informazioni all'utente.
            await genAll(window.S.profile, rg.note || '', rg.tipo);
          }
        } catch(e) { addSys('❌ Errore modifica.', 'err'); }
      }
    }
  } catch(e) {
    addMsg('ai', 'Errore: ' + e.message);
  }
  setBusy(false);
}

export function setBusy(on) {
  busy = on;
  document.getElementById('sendBtn').disabled = on;
  document.getElementById('inp').disabled = on;
  const t = document.getElementById('typing');
  if (on && !t) {
    const el = document.createElement('div'); el.id = 'typing'; el.className = 'typing-wrap';
    el.innerHTML = '<div class="dots"><div class="dot"></div><div class="dot"></div><div class="dot"></div></div>';
    document.getElementById('msgs').appendChild(el); scrollDown();
  } else if (!on && t) { t.remove(); }
  // Quando esce dallo stato busy, ri-renderizza le tile della home: setTile()
  // disabilita i bottoni 'Riprova' se busy=true; dopo setBusy(false) serve
  // rifare il render per riabilitarli, altrimenti restano cliccabili=false.
  if (!on && typeof updateDL === 'function' && window.S.profile) updateDL();
  // Restore focus alla textarea se la chat è ANCORA aperta. Evita di costringere
  // l'utente a riposizionarsi sulla casella di testo dopo ogni invio. Skip se
  // la chat è stata chiusa nel frattempo (es. genAll → home post-[RIGENERA]).
  if (!on) {
    const cp = document.getElementById('chatPanel');
    const inp = document.getElementById('inp');
    if (cp?.classList.contains('open') && inp && !inp.disabled) inp.focus();
  }
}

// ─── CHAT PANEL ────────────────────────────────────────────────────────────
// Hero empty state (logo + saluto + 3 starter cards) — visibile quando #msgs
// non contiene messaggi. Toggle via updateChatHeroVisibility() chiamato in
// openChat() e dopo ogni addMsg/addSys.
export function updateChatHeroVisibility() {
  const hero = document.getElementById('chatHero');
  const msgs = document.getElementById('msgs');
  if (!hero || !msgs) return;
  const hasRealMsg = !!msgs.querySelector('.msg');
  hero.hidden = hasRealMsg;
}

export function syncChatHeroName() {
  const nameEl = document.getElementById('chatHeroName');
  if (!nameEl) return;
  // Fonti possibili (in ordine di preferenza): headerName topbar, drawerName menu, profile.nome.
  const src = document.getElementById('headerName')?.textContent?.trim()
           || document.getElementById('drawerName')?.textContent?.trim()
           || window.S?.profile?.nome
           || '';
  if (src && src !== '…') nameEl.textContent = src;
}

// Idempotente: usa dataset.bound per non duplicare listener tra openChat ripetuti.
function _bindChatHeroCards() {
  document.querySelectorAll('.chat-hero-card').forEach(btn => {
    if (btn.dataset.bound) return;
    btn.dataset.bound = '1';
    btn.addEventListener('click', () => {
      const prompt = btn.dataset.prompt || '';
      const inp = document.getElementById('inp');
      if (!inp) return;
      inp.value = prompt;
      autoResize(inp);
      inp.focus();
      // Cursor a fine prompt — utile soprattutto per la card "Fai una domanda su…"
      // dove l'utente deve continuare a scrivere.
      try { inp.setSelectionRange(prompt.length, prompt.length); } catch (_) {}
    });
  });
}

export function openChat() {
  try { if (typeof logEvent === 'function') logEvent('chat_open', {}); } catch (_) {}
  document.body.classList.add('chat-open');
  syncChatHeroName();
  _bindChatHeroCards();
  updateChatHeroVisibility();
  document.getElementById('chatPanel').classList.add('open');
  scrollDown();
  setTimeout(() => document.getElementById('inp').focus(), 320);
}

export function closeChat() {
  document.body.classList.remove('chat-open');
  document.getElementById('chatPanel').classList.remove('open');
  updateChatHeroVisibility();
}

// ─── MSG HELPERS ───────────────────────────────────────────────────────────
export function addMsg(role, text) {
  const el = document.createElement('div'); el.className = 'msg ' + role;
  el.innerHTML = text.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/\*\*(.*?)\*\*/g,'<strong>$1</strong>').replace(/\n/g,'<br>');
  const msgs = document.getElementById('msgs'); msgs.appendChild(el);
  const t = document.getElementById('typing'); if (t) msgs.appendChild(t);
  updateChatHeroVisibility();
  scrollDown();
}

export function addSys(text, type) {
  const el = document.createElement('div'); el.className = 'msg sys' + (type ? ' ' + type : '');
  el.textContent = text;
  const msgs = document.getElementById('msgs'); msgs.appendChild(el);
  const t = document.getElementById('typing'); if (t) msgs.appendChild(t);
  updateChatHeroVisibility();
  scrollDown();
}

export function scrollDown() { const m = document.getElementById('msgs'); m.scrollTop = m.scrollHeight; }
export function autoResize(el) { el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 90) + 'px'; }
